import type { SupplierOrderDto } from '../../dto/supplierOrder';

/**
 * Loading place of a truck that collects this ZD (decision 2026-10-04):
 * the ZD's „Lokalizacja fabryki”, else the factory's address, else the
 * supplier's address. Mirror of the backend attach default
 * (services/transportOrderAttachService.js). Empty = unknown.
 */
export function resolveSupplierOrderLoadingAddress(
  supplierOrder: Pick<SupplierOrderDto, 'factoryLocation' | 'factory' | 'supplier'> | null | undefined,
): string {
  const candidates = [
    supplierOrder?.factoryLocation,
    supplierOrder?.factory?.address,
    supplierOrder?.supplier?.address,
  ];
  return candidates.map((value) => String(value || '').trim()).find(Boolean) || '';
}

// Trailing country names are not the place (usability round 2026-10-05:
// „Stambuł, Turcja” vs „Izmir, Turcja” must read as two different cities).
const COUNTRY_NAMES = new Set([
  'polska', 'poland', 'turcja', 'turkey', 'türkiye', 'turkiye', 'niemcy', 'germany', 'deutschland',
  'włochy', 'italy', 'italia', 'hiszpania', 'spain', 'españa', 'czechy', 'czech republic', 'czechia',
  'słowacja', 'slovakia', 'litwa', 'lithuania', 'łotwa', 'latvia', 'ukraina', 'ukraine', 'chiny', 'china',
  'francja', 'france', 'holandia', 'netherlands', 'belgia', 'belgium', 'austria', 'węgry', 'hungary',
  'rumunia', 'romania', 'portugalia', 'portugal', 'grecja', 'greece', 'pl', 'tr', 'de', 'it', 'es', 'cz',
]);

/**
 * City of an address for „dojdzie nowy punkt rozładunku: <miasto>”: the last
 * comma part that is not a country, without its postal code.
 */
export function placeLabel(address: string | null | undefined): string {
  const parts = String(address || '').split(',').map((part) => part.trim()).filter(Boolean);
  while (parts.length > 1 && COUNTRY_NAMES.has(parts[parts.length - 1].toLocaleLowerCase('pl-PL'))) parts.pop();
  const last = parts[parts.length - 1] || '';
  // „35-103 Rzeszów” → „Rzeszów”; „34000 Stambuł” / „Stambuł 34000” → „Stambuł”.
  const city = last.replace(/^\d{2}-\d{3}\s+/, '').replace(/^\d{4,6}\s+/, '').replace(/\s+\d{4,6}$/, '').trim();
  return city || String(address || '').trim();
}

/** Same place (case/space-insensitive), e.g. to decide whether a route point is new. */
export function samePlace(a: string | null | undefined, b: string | null | undefined): boolean {
  const key = (value: string | null | undefined) => String(value || '').replace(/\s+/g, ' ').trim().toLocaleLowerCase('pl-PL');
  return key(a) !== '' && key(a) === key(b);
}
